import { useDispatch } from "react-redux";
import {
  VStack,
  Text,
  Button,
  Checkbox,
  Box,
  useToast,
  useDisclosure,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
} from "@chakra-ui/react";
import { Formik } from "formik";
import * as Yup from "yup";
import {Button as HeadlessButton} from '@headlessui/react';

import { getUserObject } from "../redux/actions/user";
import TextInput from "../components/forms/TextInput";

import PasswordInput from "../components/forms/PasswordInput";
import LoadingSpinner from "../components/common/LoadingSpinner";
import React, { useRef, useState } from "react";

import { forgotPassword } from "./api";

import * as N from "../utilities/notifications";
import { useRouter } from "next/router";
import { saveTokenToLocalStorage, logInUser, confirmCode, resendConfirmation } from "./api";
import {newPasswordChallenge} from "./api/login";
import {FaChevronLeft} from "react-icons/fa6";
import { getErrorMessage } from "../utilities/helpers";

const LogInForm = ({
  nextURL = "",
  validatedEmail = '',
  noBorder = false,
  noRedirect = false,
}) => {
  const dispatch = useDispatch();
  const toast = useToast();
  const router = useRouter();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const formRef = useRef();
  const [rememberMe, setRememberMe] = useState(true);
  const [updatePassword, setUpdatePassword] = useState(false);
  const [verifyEmail, setVerifyEmail] = useState(false);
  const [verificationEmail, setVerificationEmail] = useState("");

  const handleResetPassword = () => {
    if (
      formRef.current.errors.pwdResetEmail ||
      formRef.current.values.pwdResetEmail === ""
    )
      return;

    forgotPassword(formRef.current.values.pwdResetEmail)
      .then((res) => {
        toast(
          N.sendCodePasswordResetSuccess(formRef.current.values.pwdResetEmail)
        );
        return true;
      })
      .catch((err) => {
        console.error("ERROR", err);
        toast(N.sendCodePasswordResetFail(err));
        return err;
      });
    onClose();
  };

  const handleSetNewPassword = async (data, setSubmitting) => {
    if(data.newPassword !== data.confirmPassword) {
      toast(N.genericFail('Unable to update password', 'Confirm password must match the new password'));
      setSubmitting(false);
    }

    try {
      const passwordUpdated = await newPasswordChallenge(data.newPassword);
      await handleSubmit({...data, password: data.newPassword}, setSubmitting, passwordUpdated);
      setUpdatePassword(false);
    }
    catch(e) {
      toast(N.genericFail('Unable to update password', getErrorMessage(e)));
      setSubmitting(false);
    }
  }

  const handleVerifyEmail = async (data, setSubmitting) => {
    try {
      await dispatch(confirmCode(verificationEmail, data.verificationCode));
      if (!data.password) {
        toast(N.genericSuccess("Email verified", "Please log in to continue."));
        setVerifyEmail(false);
        setSubmitting(false);
        return;
      }
      toast(N.verifyEmailSuccess);
      // Attempt login again after verification
      await handleSubmit({ email: verificationEmail, password: data.password, remember: rememberMe }, setSubmitting);
      setVerifyEmail(false);
    } catch (e) {
      toast(N.genericFail('Verification failed', e.message || 'The verification code is incorrect.'));
      setSubmitting(false);
    }
  }

  const handleResendCode = async () => {
    try {
      await resendConfirmation(verificationEmail);
      toast(N.resendCodeSuccess);
    } catch (e) {
      toast(N.genericFail('Failed to resend code', getErrorMessage(e)));
    }
  }

  const handleSubmit = async (data, setSubmitting, token = null) => {
    // Authenticate against Cognito
    try {
      let objUser;
      if(token === null ) {
        const authorisedUserJwtToken = await logInUser(data);
        // User Authenticated, Get user details
        objUser = await dispatch(getUserObject(authorisedUserJwtToken));
      }
      else {
        objUser = await dispatch(getUserObject(token));
      }


      // Set LocalStorage cookie if requested
      if (data.remember) {
        try {
          await saveTokenToLocalStorage(objUser.JWTToken);
        } catch (err) {
          throw new Error(err);
        }
      }

      // Send success toast
      toast(N.loginSuccess);
      if(noRedirect) {
        return true;
      }
      setUpdatePassword(false);
      if (objUser.accountType === 2 && nextURL === "") {
        await router.push("/accounts/practitioner-account/dashboard");
      } else if (nextURL !== "") {
        await router.push(nextURL);
      } else {
        await router.push("/accounts/user-account/dashboard");
      }
      return true;
    } catch (err) {
      if(err === 'newPasswordRequired') {
        setUpdatePassword(true);
        return;
      }
      if (err === 'userNotConfirmed') {
        setVerificationEmail(data.email);
        setVerifyEmail(true);
        return;
      }
      setUpdatePassword(false);
      setVerifyEmail(false);
      // Send fail toast
      console.error(`%cFAILED to log in ${err}`, "color:red; font-weight:bold");
      toast(N.loginFail(getErrorMessage(err, "Problem logging in")));
      setSubmitting(false);
      return err;
    } finally {
      // Reset formik state
      setSubmitting(false);
    }
  };

  return (
    <Formik
      innerRef={formRef}
      validateOnBlur={false}
      initialValues={{
        fullname: "",
        email: validatedEmail,
        password: "",
        newPassword: '',
        confirmPassword: "",
        pwdResetEmail: "",
        remember: rememberMe,
        verificationCode: "",
      }}
      validationSchema={Yup.object({
        email: Yup.string().email().required("Email required"),
        pwdResetEmail: Yup.string().email("Valid email address required"),
        password: Yup.string()
          .required("Password required")
          .min(8, "Password must be 8 characters or greater."),
        newPassword: updatePassword ? Yup.string()
          .required('New password required')
          .min(8, 'Password must be 8 characters or greater')
          : Yup.string(),
        confirmPassword: Yup.string(),
        verificationCode: verifyEmail ? Yup.string().required("Verification code required") : Yup.string(),
      })}
      onSubmit={(values, { setSubmitting }) => {
        values.remember = rememberMe;
        if (updatePassword) {
          handleSetNewPassword(values, setSubmitting);
        } else if (verifyEmail) {
          handleVerifyEmail(values, setSubmitting);
        }
        else {
          handleSubmit(values, setSubmitting);
        }
      }}
    >
      {(formik) => (
        <>
          {formik.isSubmitting && <LoadingSpinner message="logging in..." />}

          <Modal isOpen={isOpen} onClose={onClose}>
            <ModalOverlay />
            <ModalContent>
              <ModalHeader>Reset your password</ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                <TextInput
                  name="pwdResetEmail"
                  formlabel="Email"
                  type="email"
                  placeholder="e.g. robin@yourmail.com"
                />
              </ModalBody>

              <ModalFooter>
                <Button
                  variant="solid"
                  width="100%"
                  onClick={() => handleResetPassword()}
                >
                  Reset password
                </Button>
              </ModalFooter>
            </ModalContent>
          </Modal>
          <VStack
            as="form"
            maxW="full"
            width='full'
            py={noBorder ? 2 : 7}
            px={noBorder ? 2 : 5}
            mx="auto"
            spacing={4}
            alignItems="flex-start"
            bg="white"
            borderRadius={{ base: 0, lg: 20 }}
            onSubmit={formik.handleSubmit}
            borderColor="brand.200"
            borderWidth={noBorder ? 0 : { base: 0, lg: 0.5 }}
            overflow="hidden"
            position="relative"
          >
            {updatePassword ? (
              <>
                <HeadlessButton className='flex gap-2 items-center text-sm text-brand-alt' onClick={() => setUpdatePassword(false)}>
                  <FaChevronLeft className="h-3 w-3"/>
                  <span>Back to login</span>
                </HeadlessButton>
                <span>
                  Your account has been migrated over from our old system. This requires a password update for security reasons.
                  Please enter a new password below.
                </span>
                <PasswordInput
                  name="newPassword"
                  placeholder="8+characters"
                  formlabel="New password"
                />
                <PasswordInput
                  name="confirmPassword"
                  placeholder="8+characters"
                  formlabel="Confirm new password"
                />
              </>
            ) : verifyEmail ? (
              <>
                <HeadlessButton className='flex gap-2 items-center text-sm text-brand-alt' onClick={() => setVerifyEmail(false)}>
                  <FaChevronLeft className="h-3 w-3"/>
                  <span>Back to login</span>
                </HeadlessButton>
                <Text>
                  Please enter the verification code sent to <b>{verificationEmail}</b>.
                </Text>
                <TextInput
                  name="verificationCode"
                  formlabel="Verification code"
                  type="text"
                  placeholder="123456"
                />
                <Button
                  variant="link"
                  fontSize="sm"
                  bgGradient="linear(to-r, primary.600, primary.500)"
                  bgClip="text"
                  onClick={handleResendCode}
                >
                  Resend verification code
                </Button>
              </>
            ) : (
              <>
                <TextInput
                  name="email"
                  formlabel="Email"
                  type="email"
                  placeholder="e.g. robin@yourmail.com"
                />
                <PasswordInput
                  name="password"
                  placeholder="8+characters"
                  formlabel="Password"
                />
              </>
            )}
            {!updatePassword && !verifyEmail && (
              <Box display="flex" w="100%" justifyContent="space-between">
                <Checkbox
                  name="remember"
                  textStyle="h7"
                  color="brand.300"
                  size="md"
                  defaultChecked
                  colorScheme="secondary"
                  onChange={(e) => setRememberMe(e.target.checked)}
                >
                  <Text textStyle="h6">Remember me</Text>
                </Checkbox>

                <Button
                  variant="link"
                  fontSize="sm"
                  bgGradient="linear(to-r, primary.600, primary.500)"
                  bgClip="text"
                  onClick={onOpen}
                >
                  Forgot my password
                </Button>
              </Box>
            )}
            <Box p="1rem 0 0 0" w="100%">
              <Button w="full" variant="solid" type="submit">
                {updatePassword ? 'Set new password' : verifyEmail ? 'Verify email' : 'Log in'}
              </Button>
            </Box>
          </VStack>
        </>
      )}
    </Formik>
  );
};

export default LogInForm;
